/* ═══════════════════════════════════════════════════════════════
   spacing-geometry — the MEASURED gap math behind every Studio
   spacing band.

   Before this module the level spacing bar was PREDICTED: it parsed
   the level's `marginTop` / `marginBottom` back out of the CSS the
   sample paragraph was given and drew a grip at half that value.
   CSS margin collapsing means the real gap between two paragraphs is
   `max(above.marginBottom, below.marginTop)`, not either one alone —
   so the predicted bar landed on top of the paragraphs instead of in
   the gap (Josh, 2026-08-22: the band cut through level 7/8).

   The fix is to stop predicting. These helpers turn real
   `getBoundingClientRect()` readings into a band rectangle expressed
   in the host's own LAYOUT pixels (the Studio canvas is scaled by a
   CSS zoom transform, so client pixels are not layout pixels).

   Pure, no React, no DOM types beyond the two numbers each rect
   contributes — the whole point is that this stays testable without
   a browser.
   ═══════════════════════════════════════════════════════════════ */

/** The only part of a DOMRect this math needs. */
export interface EdgeRect {
  top: number;
  bottom: number;
}

/**
 * The zoom factor a transformed ancestor is applying, derived from the
 * host itself: `getBoundingClientRect().width` is post-transform,
 * `offsetWidth` is pre-transform. Falls back to 1 for anything
 * degenerate (detached node, zero-width host, jsdom).
 */
export function measuredScale(renderedWidth: number, layoutWidth: number): number {
  if (!layoutWidth || !renderedWidth) return 1;
  const s = renderedWidth / layoutWidth;
  return Number.isFinite(s) && s > 0.01 ? s : 1;
}

export interface GapGeometry {
  /** Band top, in host layout px, relative to the host's border box. */
  top: number;
  /** Band height, in host layout px. Never negative. */
  height: number;
}

/**
 * The rectangle that EXACTLY fills the space between two stacked
 * elements. `above`/`below` are the elements bounding the gap; either
 * may be null, in which case the host's own edge is used (the gap
 * above the first paragraph, or below the last one).
 *
 * All inputs are client-space rects; the result is host layout px.
 */
export function gapGeometry(
  host: EdgeRect,
  above: EdgeRect | null,
  below: EdgeRect | null,
  scale = 1
): GapGeometry {
  const s = measuredScale(scale, 1);
  const topClient = above ? above.bottom : host.top;
  const bottomClient = below ? below.top : host.bottom;
  return {
    top: (topClient - host.top) / s,
    height: Math.max(0, (bottomClient - topClient) / s),
  };
}

/** Horizontal offset of a child's box inside the host, in host layout px. */
export function offsetWithin(hostLeft: number, childLeft: number, scale = 1): number {
  const s = measuredScale(scale, 1);
  return (childLeft - hostLeft) / s;
}

/** CSS px → points (the unit every forma spacing field stores). */
export const pxToPt = (px: number): number => (px * 72) / 96;

/** Points → CSS px. Mirrors `shared/body-layout.ts`'s `ptToPx`. */
export const ptToPxLocal = (pt: number): number => (pt * 96) / 72;

/**
 * The hit-area padding a band needs above and below its visual fill so
 * the grab target clears the Fitts's-law floor (DESIGN §4 — 32px).
 * A 0pt gap draws as a hairline but must still be grabbable.
 */
export function bandHitPadding(height: number, floor = 32): number {
  return Math.max(0, (floor - height) / 2);
}
